import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Globe } from 'lucide-react';
import { LanguageSelector, useLanguage } from '../contexts/LanguageContext';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [toursDropdown, setToursDropdown] = useState(false);
  const location = useLocation();
  const { t, language } = useLanguage();

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="bg-white border-b border-gray-100 sticky top-0 z-40 shadow-sm">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <span className="text-4xl md:text-5xl font-extrabold text-gray-900 tracking-tight">
              istanbul<span className="text-orange-500">turda</span>
            </span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-6 lg:space-x-8">
            <Link
              to="/"
              data-testid="nav-/"
              className={`text-sm font-bold transition-colors hover:text-orange-500 ${
                isActive('/') ? 'text-orange-500' : 'text-gray-700'
              }`}
            >
              {t('nav.home')}
            </Link>

            {/* Tours Dropdown */}
            <div 
              className="relative group"
              onMouseEnter={() => setToursDropdown(true)}
              onMouseLeave={() => setToursDropdown(false)}
            >
              <button
                className="text-sm font-bold text-gray-700 hover:text-orange-500 transition-colors flex items-center space-x-1 py-2"
                data-testid="nav-tours-dropdown"
              >
                <span>{t('nav.tours')}</span>
                <ChevronDown size={16} className={`transition-transform duration-200 ${toursDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {toursDropdown && (
                <div className="absolute top-full left-0 pt-1 w-56 z-50">
                  {/* Invisible bridge to prevent hover gap */}
                  <div className="absolute -top-1 left-0 w-full h-2"></div>
                  <div className="bg-white rounded-lg shadow-xl border border-gray-100 py-2">
                    <Link
                      to="/turlar/istanbul"
                      onClick={() => setToursDropdown(false)}
                      className="block px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-orange-50 hover:text-orange-500 transition-colors"
                      data-testid="nav-istanbul-tours"
                    >
                      {t('nav.istanbul')}
                    </Link>
                    <Link
                      to="/turlar/yurtici"
                      onClick={() => setToursDropdown(false)}
                      className="block px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-orange-50 hover:text-orange-500 transition-colors"
                      data-testid="nav-yurtici-tours"
                    >
                      {t('nav.domestic')}
                    </Link>
                    <Link
                      to="/turlar/yurtdisi"
                      onClick={() => setToursDropdown(false)}
                      className="block px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-orange-50 hover:text-orange-500 transition-colors"
                      data-testid="nav-yurtdisi-tours"
                    >
                      {t('nav.international')}
                    </Link>
                    <div className="border-t border-gray-100 mt-2 pt-2">
                      <Link
                        to="/turlar"
                        onClick={() => setToursDropdown(false)}
                        className="block px-4 py-3 text-sm font-bold text-orange-500 hover:bg-orange-50 transition-colors"
                        data-testid="nav-all-tours"
                      >
                        {t('nav.all_tours')}
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/istanbul-hikayeleri"
              data-testid="nav-/istanbul-hikayeleri"
              className={`text-sm font-bold transition-colors hover:text-orange-500 ${
                isActive('/istanbul-hikayeleri') ? 'text-orange-500' : 'text-gray-700'
              }`}
            >
              {t('nav.blog')}
            </Link>

            <Link
              to="/hakkimizda"
              data-testid="nav-/hakkimizda"
              className={`text-sm font-bold transition-colors hover:text-orange-500 ${
                isActive('/hakkimizda') ? 'text-orange-500' : 'text-gray-700'
              }`}
            >
              {t('nav.about')}
            </Link>

            <Link
              to="/iletisim"
              data-testid="nav-/iletisim"
              className={`text-sm font-bold transition-colors hover:text-orange-500 ${
                isActive('/iletisim') ? 'text-orange-500' : 'text-gray-700'
              }`}
            >
              {t('nav.contact')}
            </Link>

            <Link
              to="/admin"
              data-testid="nav-admin"
              className={`text-sm font-bold transition-colors hover:text-orange-500 ${
                isActive('/admin') ? 'text-orange-500' : 'text-gray-700'
              }`}
            >
              {t('nav.admin') || 'Yönetim'}
            </Link>

            {/* Language Selector */}
            <LanguageSelector className="ml-2" />
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="mobile-menu-button"
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden pb-6 space-y-4" data-testid="mobile-menu">
            <Link
              to="/"
              onClick={() => setIsOpen(false)}
              className={`block text-base font-medium transition-colors hover:text-orange-500 ${
                isActive('/') ? 'text-orange-500' : 'text-gray-700'
              }`}
            >
              {t('nav.home')}
            </Link>
            <Link
              to="/turlar/istanbul"
              onClick={() => setIsOpen(false)}
              className="block text-base font-medium text-gray-700 hover:text-orange-500 transition-colors pl-4"
            >
              {t('nav.istanbul')}
            </Link>
            <Link
              to="/turlar/yurtici"
              onClick={() => setIsOpen(false)}
              className="block text-base font-medium text-gray-700 hover:text-orange-500 transition-colors pl-4"
            >
              {t('nav.domestic')}
            </Link>
            <Link
              to="/turlar/yurtdisi"
              onClick={() => setIsOpen(false)}
              className="block text-base font-medium text-gray-700 hover:text-orange-500 transition-colors pl-4"
            >
              {t('nav.international')}
            </Link>
            <Link
              to="/istanbul-hikayeleri"
              onClick={() => setIsOpen(false)}
              className={`block text-base font-medium transition-colors hover:text-orange-500 ${
                isActive('/istanbul-hikayeleri') ? 'text-orange-500' : 'text-gray-700'
              }`}
            >
              {t('nav.blog')}
            </Link>
            <Link
              to="/hakkimizda"
              onClick={() => setIsOpen(false)}
              className={`block text-base font-medium transition-colors hover:text-orange-500 ${
                isActive('/hakkimizda') ? 'text-orange-500' : 'text-gray-700'
              }`}
            >
              {t('nav.about')}
            </Link>
            <Link
              to="/iletisim"
              onClick={() => setIsOpen(false)}
              className={`block text-base font-medium transition-colors hover:text-orange-500 ${
                isActive('/iletisim') ? 'text-orange-500' : 'text-gray-700'
              }`}
            >
              {t('nav.contact')}
            </Link>
            <Link
              to="/admin"
              onClick={() => setIsOpen(false)}
              className="block text-base font-medium text-white bg-orange-500 hover:bg-orange-600 px-4 py-2 rounded-full text-center mt-4"
            >
              {t('nav.admin') || 'Yönetim Paneli'}
            </Link>
            {/* Mobile Language Selector */}
            <div className="pt-4 border-t border-gray-200">
              <LanguageSelector className="w-full" />
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
